import './WorkTaskFiles.scss';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../../contexts/auth';
import {
  WorkTaskFileDto,
  getWorkTaskFiles,
  uploadWorkTaskFiles,
  deleteWorkTaskFile,
  getFileUrl,
} from '../../../api/work-task-files';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

function isImage(contentType: string): boolean {
  return IMAGE_TYPES.includes(contentType?.toLowerCase());
}

function getExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

// ─── Authenticated image component ─────────────────────────────────────────
interface AuthImgProps {
  src: string;
  token: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}
const AuthImg = ({ src, token, alt, className, style, onClick }: AuthImgProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => { /* silently fail */ });
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src, token]);

  if (!blobUrl) return <div className='file-placeholder' />;
  return <img src={blobUrl} alt={alt} className={className} style={style} onClick={onClick} />;
};
// ────────────────────────────────────────────────────────────────────────────

interface WorkTaskFilesProps {
  workTaskOid: string;
}

export const WorkTaskFiles = ({ workTaskOid }: WorkTaskFilesProps) => {
  const { user } = useAuth();
  const [files, setFiles] = useState<WorkTaskFileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxFile, setLightboxFile] = useState<WorkTaskFileDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = useCallback(async() => {
    if (!user?.token || !workTaskOid) return;
    setLoading(true);
    try {
      const data = await getWorkTaskFiles(user.token, workTaskOid);
      setFiles(data);
    } catch {
      notify('Bestanden laden mislukt.', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, [user?.token, workTaskOid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const onUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFilesSelected = useCallback(async(e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    if (!user?.token) return;

    const maxSize = 10 * 1024 * 1024;
    for (const f of selectedFiles) {
      if (f.size > maxSize) {
        notify(`Bestand '${f.name}' is groter dan 10 MB.`, 'error', 4000);
        return;
      }
    }

    setUploading(true);
    try {
      await uploadWorkTaskFiles(user.token, workTaskOid, selectedFiles);
      await loadFiles();
      notify(`${selectedFiles.length} bestand(en) geüpload.`, 'success', 3000);
    } catch {
      notify('Upload mislukt.', 'error', 3000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [user?.token, workTaskOid, loadFiles]);

  const onDeleteFile = useCallback(async(file: WorkTaskFileDto) => {
    if (!user?.token) return;
    try {
      await deleteWorkTaskFile(user.token, workTaskOid, file.Oid);
      setFiles((prev) => prev.filter((p) => p.Oid !== file.Oid));
      if (lightboxFile?.Oid === file.Oid) setLightboxFile(null);
      notify('Bestand verwijderd.', 'success', 2000);
    } catch {
      notify('Verwijderen mislukt.', 'error', 3000);
    }
  }, [user?.token, workTaskOid, lightboxFile]);

  const onFileClick = useCallback((file: WorkTaskFileDto) => {
    if (isImage(file.ContentType)) {
      setLightboxFile(file);
    } else {
      // Non-image: download via authenticated fetch
      if (!user?.token) return;
      const url = getFileUrl(workTaskOid, file.Oid);
      fetch(url, { headers: { Authorization: `Bearer ${user.token}` } })
        .then((r) => r.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = file.FileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        })
        .catch(() => notify('Download mislukt.', 'error', 3000));
    }
  }, [user?.token, workTaskOid]);

  const closeLightboxOptions = { text: 'Sluiten', onClick: () => setLightboxFile(null) };

  return (
    <div className='work-task-files'>
      <div className='files-toolbar'>
        <Button
          text={uploading ? 'Bezig...' : 'Bestanden uploaden'}
          icon='upload'
          type='default'
          disabled={uploading}
          onClick={onUploadClick}
        />
        <input
          ref={fileInputRef}
          type='file'
          accept='.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx'
          multiple
          style={{ display: 'none' }}
          onChange={onFilesSelected}
        />
      </div>

      {loading ? (
        <div className='files-loading'>
          <LoadIndicator visible />
        </div>
      ) : files.length === 0 ? (
        <div className='files-empty'>
          <i className='dx-icon-folder files-empty-icon' />
          <p>Geen bestanden beschikbaar</p>
        </div>
      ) : (
        <div className='files-grid'>
          {files.map((file) => (
            <div key={file.Oid} className='file-tile'>
              {isImage(file.ContentType) ? (
                <AuthImg
                  src={getFileUrl(workTaskOid, file.Oid)}
                  token={user?.token ?? ''}
                  alt={file.FileName}
                  onClick={() => onFileClick(file)}
                />
              ) : (
                <div className='file-icon-container' onClick={() => onFileClick(file)}>
                  <i className='dx-icon-file' />
                  <span className='file-ext'>{getExtension(file.FileName)}</span>
                </div>
              )}
              <div className='file-overlay'>
                <span className='file-name'>{file.FileName}</span>
                <Button
                  icon='trash'
                  stylingMode='text'
                  type='danger'
                  hint='Verwijderen'
                  onClick={(e) => { e.event?.stopPropagation(); onDeleteFile(file); }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Popup
        visible={!!lightboxFile}
        onHiding={() => setLightboxFile(null)}
        dragEnabled={false}
        hideOnOutsideClick
        showTitle
        title={lightboxFile?.FileName ?? ''}
        width='90vw'
        height='90vh'
        maxWidth={1200}
      >
        <ToolbarItem widget='dxButton' toolbar='bottom' location='center' options={closeLightboxOptions} />
        {lightboxFile && (
          <AuthImg
            src={getFileUrl(workTaskOid, lightboxFile.Oid)}
            token={user?.token ?? ''}
            alt={lightboxFile.FileName}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        )}
      </Popup>
    </div>
  );
};
